import { useState, useEffect, useCallback } from 'react';
import { projectCostingAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import {
  Plus, Loader2, TrendingUp, TrendingDown, DollarSign, Clock,
  ArrowLeft, Trash2, IndianRupee, Briefcase, BarChart3, Target,
} from 'lucide-react';

const COST_CATEGORIES = [
  { value: 'salary', label: 'Salary & Wages' },
  { value: 'tools', label: 'Tools & Software' },
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'travel', label: 'Travel' },
  { value: 'misc', label: 'Miscellaneous' },
];
const PIE_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6'];
const STATUS_COLORS = { active: 'bg-green-100 text-green-700', completed: 'bg-blue-100 text-blue-700', on_hold: 'bg-yellow-100 text-yellow-700' };

const formatINR = (v) => {
  if (!v && v !== 0) return '-';
  const n = Number(v);
  if (isNaN(n)) return '-';
  if (n >= 10000000) return `${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return n.toLocaleString('en-IN');
};

export default function ProjectCostingPage() {
  const { user } = useAuth();
  const canManage = ['super_admin', 'admin', 'accounts'].includes(user?.role);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDetail, setProjectDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', client: '', billing_rate_per_hour: 0, budget: 0, start_date: '', end_date: '', status: 'active', description: '', currency: 'INR' });
  const [costForm, setCostForm] = useState({ project_id: '', category: 'salary', description: '', amount: 0, date: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [projRes, sumRes] = await Promise.all([
        projectCostingAPI.getProjects(),
        projectCostingAPI.getSummary()
      ]);
      setProjects(projRes.data);
      setSummary(sumRes.data);
    } catch (e) { toast.error('Failed to load projects'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadProjectDetail = async (projectId) => {
    setDetailLoading(true);
    setSelectedProject(projectId);
    try {
      const res = await projectCostingAPI.getProject(projectId);
      setProjectDetail(res.data);
    } catch (e) { toast.error('Failed to load project'); }
    finally { setDetailLoading(false); }
  };

  const saveProject = async () => {
    if (!form.name) { toast.error('Project name required'); return; }
    setSaving(true);
    try {
      await projectCostingAPI.createProject(form);
      toast.success('Project created');
      setDialogOpen(false);
      setForm({ name: '', client: '', billing_rate_per_hour: 0, budget: 0, start_date: '', end_date: '', status: 'active', description: '', currency: 'INR' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create project'); }
    finally { setSaving(false); }
  };

  const saveCostEntry = async () => {
    if (!costForm.amount || !costForm.project_id) { toast.error('Amount and project required'); return; }
    setSaving(true);
    try {
      await projectCostingAPI.addCostEntry(costForm);
      toast.success('Cost entry added');
      setCostDialogOpen(false);
      setCostForm({ project_id: '', category: 'salary', description: '', amount: 0, date: '' });
      fetchData();
      if (selectedProject) loadProjectDetail(selectedProject);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add cost'); }
    finally { setSaving(false); }
  };

  const deleteCostEntry = async (entryId) => {
    if (!confirm('Delete this cost entry?')) return;
    try {
      await projectCostingAPI.deleteCostEntry(entryId);
      toast.success('Cost entry deleted');
      if (selectedProject) loadProjectDetail(selectedProject);
      fetchData();
    } catch (e) { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="space-y-6"><Skeleton className="h-10 w-64" /><Skeleton className="h-96" /></div>;

  // If viewing project detail
  if (selectedProject && projectDetail) {
    return (
      <div data-testid="project-detail-page" className="space-y-6 animate-fadeIn">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => { setSelectedProject(null); setProjectDetail(null); }} data-testid="back-to-projects-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-['Manrope']">{projectDetail.name}</h1>
            <div className="text-muted-foreground flex items-center gap-2">{projectDetail.client} &bull; <Badge className={STATUS_COLORS[projectDetail.status]}>{projectDetail.status}</Badge></div>
          </div>
          {canManage && (
            <Button className="ml-auto" onClick={() => { setCostForm({ project_id: selectedProject, category: 'salary', description: '', amount: 0, date: new Date().toISOString().slice(0, 10) }); setCostDialogOpen(true); }} data-testid="add-cost-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Cost
            </Button>
          )}
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <MetricCard label="Budget" value={`Rs. ${formatINR(projectDetail.budget)}`} icon={Target} />
          <MetricCard label="Total Cost" value={`Rs. ${formatINR(projectDetail.total_cost)}`} icon={TrendingDown} color="text-red-600" />
          <MetricCard label="Revenue" value={`Rs. ${formatINR(projectDetail.total_revenue)}`} icon={TrendingUp} color="text-green-600" />
          <MetricCard label="Profit" value={`Rs. ${formatINR(projectDetail.profit)}`} icon={DollarSign} color={projectDetail.profit >= 0 ? 'text-green-600' : 'text-red-600'} />
          <MetricCard label="Margin" value={`${projectDetail.margin_pct}%`} icon={BarChart3} color={projectDetail.margin_pct >= 0 ? 'text-green-600' : 'text-red-600'} />
          <MetricCard label="Hours" value={`${projectDetail.total_hours}h`} icon={Clock} sub={`${projectDetail.billable_hours}h billable`} />
        </div>

        {/* Budget Utilization Bar */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="font-medium">Budget Utilization</span>
              <span className={projectDetail.budget_utilization > 100 ? 'text-red-600 font-bold' : 'text-muted-foreground'}>{projectDetail.budget_utilization}%</span>
            </div>
            <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${projectDetail.budget_utilization > 100 ? 'bg-red-500' : projectDetail.budget_utilization > 80 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{ width: `${Math.min(projectDetail.budget_utilization, 100)}%` }} />
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Cost by Category Chart */}
          {projectDetail.cost_by_category?.length > 0 && (
            <Card data-testid="cost-category-chart">
              <CardHeader className="pb-2"><CardTitle className="text-base">Cost by Category</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={projectDetail.cost_by_category} dataKey="amount" nameKey="category" cx="50%" cy="50%" innerRadius={50} outerRadius={85} label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                      {projectDetail.cost_by_category.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `Rs. ${formatINR(v)}`} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Monthly Hours Chart */}
          {projectDetail.monthly_hours?.length > 0 && (
            <Card data-testid="monthly-hours-chart">
              <CardHeader className="pb-2"><CardTitle className="text-base">Monthly Hours</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={projectDetail.monthly_hours}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="billable_hours" name="Billable" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="hours" name="Total" fill="#c4b5fd" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Cost Entries Table */}
        <Card>
          <CardHeader><CardTitle className="text-base">Cost Entries</CardTitle><CardDescription>{projectDetail.cost_entries?.length || 0} entries</CardDescription></CardHeader>
          <CardContent>
            {projectDetail.cost_entries?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-muted/50"><th className="p-2 text-left">Date</th><th className="p-2 text-left">Category</th><th className="p-2 text-left">Description</th><th className="p-2 text-right">Amount</th><th className="p-2"></th></tr></thead>
                  <tbody>
                    {projectDetail.cost_entries.map(entry => (
                      <tr key={entry.id} className="border-b hover:bg-muted/30">
                        <td className="p-2">{entry.date}</td>
                        <td className="p-2"><Badge variant="outline">{entry.category}</Badge></td>
                        <td className="p-2 text-muted-foreground">{entry.description}</td>
                        <td className="p-2 text-right font-medium">Rs. {Number(entry.amount).toLocaleString('en-IN')}</td>
                        <td className="p-2">{canManage && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteCostEntry(entry.id)} data-testid={`delete-cost-${entry.id}`}><Trash2 className="h-3.5 w-3.5 text-muted-foreground" /></Button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-center py-8 text-muted-foreground">No cost entries yet</p>}
          </CardContent>
        </Card>

        {/* Add Cost Dialog */}
        <CostEntryDialog open={costDialogOpen} onOpenChange={setCostDialogOpen} form={costForm} setForm={setCostForm} onSave={saveCostEntry} saving={saving} />
      </div>
    );
  }

  // Projects List View
  return (
    <div data-testid="project-costing-page" className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Project Costing</h1>
          <p className="text-muted-foreground mt-1">Track costs, revenue, and profitability across projects</p>
        </div>
        {canManage && (
          <Button onClick={() => setDialogOpen(true)} data-testid="new-project-btn"><Plus className="h-4 w-4 mr-1" /> New Project</Button>
        )}
      </div>

      {/* Summary KPIs */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard label="Active Projects" value={summary.total_projects} icon={Briefcase} />
          <MetricCard label="Total Revenue" value={`Rs. ${formatINR(summary.total_revenue)}`} icon={TrendingUp} color="text-green-600" />
          <MetricCard label="Total Cost" value={`Rs. ${formatINR(summary.total_cost)}`} icon={TrendingDown} color="text-red-600" />
          <MetricCard label="Avg Margin" value={`${summary.avg_margin}%`} icon={BarChart3} color={summary.avg_margin >= 0 ? 'text-green-600' : 'text-red-600'} />
        </div>
      )}

      {/* Profitability Chart */}
      {summary?.projects?.length > 0 && (
        <Card data-testid="profitability-chart">
          <CardHeader className="pb-2"><CardTitle className="text-base">Project Profitability</CardTitle><CardDescription>Revenue vs Cost by project</CardDescription></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={summary.projects} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => formatINR(v)} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `Rs. ${formatINR(v)}`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="revenue" name="Revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="cost" name="Cost" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Projects Table */}
      <Card>
        <CardHeader><CardTitle className="text-base">Projects</CardTitle><CardDescription>{projects.length} projects</CardDescription></CardHeader>
        <CardContent>
          {projects.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="p-3 text-left">Project</th><th className="p-3 text-left">Client</th>
                    <th className="p-3 text-right">Budget</th><th className="p-3 text-right">Cost</th>
                    <th className="p-3 text-right">Revenue</th><th className="p-3 text-right">Profit</th>
                    <th className="p-3 text-right">Margin</th><th className="p-3 text-right">Hours</th>
                    <th className="p-3 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map(p => (
                    <tr key={p.id} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => loadProjectDetail(p.id)} data-testid={`project-row-${p.id}`}>
                      <td className="p-3 font-medium">{p.name}</td>
                      <td className="p-3 text-muted-foreground">{p.client}</td>
                      <td className="p-3 text-right">Rs. {formatINR(p.budget)}</td>
                      <td className="p-3 text-right text-red-600">Rs. {formatINR(p.total_cost)}</td>
                      <td className="p-3 text-right text-green-600">Rs. {formatINR(p.total_revenue)}</td>
                      <td className={`p-3 text-right font-medium ${p.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>Rs. {formatINR(p.profit)}</td>
                      <td className={`p-3 text-right font-medium ${p.margin_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>{p.margin_pct}%</td>
                      <td className="p-3 text-right">{p.total_hours}h</td>
                      <td className="p-3 text-center"><Badge className={STATUS_COLORS[p.status]}>{p.status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Briefcase className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">No projects yet</p>
              <p className="text-sm mt-1">Create a project to start tracking costs and profitability</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Project Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Project</DialogTitle>
            <DialogDescription>Set up cost and revenue tracking for a project</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Project Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Project name" data-testid="project-name-input" /></div>
              <div className="space-y-2"><Label>Client</Label><Input value={form.client} onChange={e => setForm({...form, client: e.target.value})} placeholder="Client name" data-testid="project-client-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Budget (Rs.)</Label><Input type="number" value={form.budget} onChange={e => setForm({...form, budget: parseFloat(e.target.value) || 0})} data-testid="project-budget-input" /></div>
              <div className="space-y-2"><Label>Billing Rate/Hr (Rs.)</Label><Input type="number" value={form.billing_rate_per_hour} onChange={e => setForm({...form, billing_rate_per_hour: parseFloat(e.target.value) || 0})} data-testid="project-rate-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} /></div>
              <div className="space-y-2"><Label>End Date</Label><Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} /></div>
            </div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} placeholder="Project description" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveProject} disabled={saving} data-testid="save-project-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Cost Entry Dialog */}
      <CostEntryDialog open={costDialogOpen} onOpenChange={setCostDialogOpen} form={costForm} setForm={setCostForm} onSave={saveCostEntry} saving={saving} />
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, color = '', sub }) {
  return (
    <Card>
      <CardContent className="pt-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-muted"><Icon className={`h-5 w-5 ${color || 'text-primary'}`} /></div>
          <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className={`text-lg font-bold ${color}`}>{value}</p>
            {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CostEntryDialog({ open, onOpenChange, form, setForm, onSave, saving }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add Cost Entry</DialogTitle><DialogDescription>Record an expense for this project</DialogDescription></DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                <SelectTrigger data-testid="cost-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{COST_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Amount (Rs.)</Label><Input type="number" value={form.amount} onChange={e => setForm({...form, amount: parseFloat(e.target.value) || 0})} data-testid="cost-amount-input" /></div>
          </div>
          <div className="space-y-2"><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="cost-date-input" /></div>
          <div className="space-y-2"><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="What was this expense for?" data-testid="cost-desc-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} data-testid="save-cost-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Add Entry</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
